import { Link, Outlet, useLocation } from 'react-router-dom';
import { ShieldAlert, Users, Building2, ScrollText } from 'lucide-react';
import { useCurrentUser } from '../../lib/api/queries';

/**
 * Layout shell for every /admin/* page. Renders a sub-nav (Users / Tenants)
 * and an access-denied banner if a non-admin somehow lands here. Server-side
 * gating (SuperAdmin policy on /api/admin/*) is the real defence — this UI
 * gate just keeps the experience clean.
 */
export function AdminShell() {
  const { data: me } = useCurrentUser();
  const location = useLocation();

  if (!me?.isPlatformAdmin) {
    return (
      <div className="flex items-center gap-3 bg-warning-50 border border-warning-500/30
                      text-warning-600 rounded-md p-4">
        <ShieldAlert className="w-5 h-5 shrink-0" aria-hidden />
        <p className="text-sm">
          This page is for platform administrators. If you think you should have access,
          ping the DPAS operations team.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <nav className="flex gap-1 border-b border-gray-200">
        <SubLink to="/admin/users"   icon={<Users className="w-4 h-4" />}
                 active={location.pathname.startsWith('/admin/users')
                         || location.pathname === '/admin'}>
          Users
        </SubLink>
        <SubLink to="/admin/tenants" icon={<Building2 className="w-4 h-4" />}
                 active={location.pathname.startsWith('/admin/tenants')}>
          Tenants
        </SubLink>
        <SubLink to="/admin/log"     icon={<ScrollText className="w-4 h-4" />}
                 active={location.pathname.startsWith('/admin/log')}>
          Audit log
        </SubLink>
      </nav>
      <Outlet />
    </div>
  );
}

function SubLink({
  to, active, icon, children,
}: {
  to: string;
  active: boolean;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      className={
        'inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium border-b-2 -mb-px ' +
        'transition-colors ' +
        (active
          ? 'border-accent-alt-600 text-accent-alt-700'
          : 'border-transparent text-gray-600 hover:text-gray-900 hover:border-gray-300')
      }
    >
      {icon}
      {children}
    </Link>
  );
}
